import type { SedeConfig } from './types'

/**
 * Verde Vercelli — Andrea Vetri's sede.
 * Legal entity: ditta individuale "Verde Vercelli di Andrea Vetri"
 */
export const verdeVercelli: SedeConfig = {
  slug: 'verde-vercelli',
  domains: ['verdevercelli.it', 'www.verdevercelli.it'],
  sedeId: 1,
  brand: {
    name: 'Verde Vercelli',
    tagline: 'Manutenzione giardini nel Vercellese',
    primaryRgb: '1 50 32',          // #013220 (default AVMG dark green)
    primaryHoverRgb: '1 100 50',    // #016432
    inkRgb: '255 255 255',
  },
  contact: {
    email: 'info@verdevercelli.it',
    privacyEmail: 'privacy@verdevercelli.it',
    phone: '+39 348 438 4139',
    address: 'Via dei Ciudin 8, 13100 Vercelli',
    piva: '02482010028',
    addressStreet: 'Via dei Ciudin 8',
    postalCode: '13100',
  },
  founder: {
    name: 'Andrea Vetri',
    role: 'Titolare',
    foundedYear: 2011,
  },
  legal: {
    legalName: 'Verde Vercelli di Andrea Vetri',
    piva: '02482010028',
  },
  // The shared legal section (src/legal/). No English home yet, so the
  // English legal pages send "← Site" to the Italian one.
  legalPages: {
    locales: ['it', 'en'],
    legalForm: 'Ditta individuale',
    city: 'Vercelli (VC)',
    lastUpdated: '2026-10-16', // privacy/cookie/deletion corrected to the 2026-10-16 legal-corpus audit (§7.18)
    // Own WhatsApp channel answered by the AI assistant: every Sede has its own number (no network default).
    whatsappAi: true,
    // the Vercelli site never fetches the Google rating (no googleRating import under sites/verde-vercelli).
    googlePlaces: false,
    // The management-platform provider (AVMG), the Sede's processor (art. 28 GDPR).
    platformProvider: 'LEONARDO RICCARDO RAVOT',
    homePath: { it: '/', en: '/' },
    header: { logoSrc: '/media/verde-vercelli/logo.png', tagline: 'di Andrea Vetri' },
    // Also the key a future light theme on the Vercelli site must use (see components/Header.tsx).
    themeStorageKey: 'verde-vercelli-theme',
    credits: {
      entries: [],
      notes: {
        it: [
          'Foto, video e grafica: Verde Vercelli.',
          'Caratteri tipografici: Inter e JetBrains Mono, rilasciati con licenza SIL Open Font License 1.1.',
        ],
        en: [
          'Photos, videos and graphics: Verde Vercelli.',
          'Typefaces: Inter and JetBrains Mono, released under the SIL Open Font License 1.1.',
        ],
      },
    },
  },
  seo: {
    title: 'Verde Vercelli — Manutenzione Giardini e Spazi Verdi nel Vercellese',
    description: "Verde Vercelli di Andrea Vetri. Manutenzione giardini, potatura, progettazione e cura del verde per privati, condomini e aziende a Vercelli e provincia. Dal 2011.",
    keywords: 'manutenzione giardini Vercelli, giardiniere Vercelli, potatura alberi Vercelli, cura verde Vercelli, Verde Vercelli, Andrea Vetri, taglio prato, tosatura siepi, tree climbing, abbattimento alberi, progettazione giardini, pulizia giardino, fresatura ceppi',
    ogTitle: 'Verde Vercelli — Manutenzione Giardini e Spazi Verdi',
    ogImage: 'https://verdevercelli.it/media/verde-vercelli/og-image.jpg',
    ogImageAlt: 'Verde Vercelli — Manutenzione giardini nel Vercellese',
    canonical: 'https://verdevercelli.it/',
    geo: {
      region: 'IT-VC',
      placename: 'Vercelli',
      lat: 45.3225,
      lon: 8.4186,
    },
    themeColor: '#013220',
    appleTitle: 'Verde Vercelli',
    googleVerification: 'jtvSPDYpfkBAODGVVv7f3qx94RtLwf5nOe3ovkWHrb8',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'LandscapingBusiness',
        name: 'Verde Vercelli di Andrea Vetri',
        alternateName: 'Verde Vercelli',
        description: 'Manutenzione giardini, potatura, progettazione e cura professionale del verde per privati, condomini e aziende a Vercelli e provincia.',
        url: 'https://verdevercelli.it',
        logo: 'https://verdevercelli.it/favicon.svg',
        image: 'https://verdevercelli.it/media/verde-vercelli/og-image.jpg',
        telephone: '+393484384139',
        email: 'info@verdevercelli.it',
        foundingDate: '2011',
        founder: {
          '@type': 'Person',
          name: 'Andrea Vetri',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via dei Ciudin 8',
          addressLocality: 'Vercelli',
          postalCode: '13100',
          addressRegion: 'Piemonte',
          addressCountry: 'IT',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 45.3225,
          longitude: 8.4186,
        },
        areaServed: [
          { '@type': 'City', name: 'Vercelli' },
          { '@type': 'AdministrativeArea', name: 'Provincia di Vercelli' },
        ],
        priceRange: '€€',
        currenciesAccepted: 'EUR',
        paymentAccepted: 'Contanti, Bonifico bancario',
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '30',
          bestRating: '5',
        },
        knowsAbout: [
          'Manutenzione giardini',
          'Potatura alberi',
          'Tosatura siepi',
          'Tree climbing',
          'Abbattimento controllato',
          'Progettazione giardini',
          'Taglio prato',
          'Fresatura ceppi',
          'Pulizia post lavori',
          'Topiarie artistiche',
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Servizi di manutenzione verde',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Manutenzione giardini',
                description: 'Taglio prato, arieggiatura, concimazione e cura programmata',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Potatura e abbattimento',
                description: 'Tree-climbing certificato, abbattimenti in sicurezza, potatura ornamentale',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Progettazione giardini',
                description: 'Dal concept alla messa a dimora, studio del terreno e integrazione paesaggistica',
              },
            },
          ],
        },
        sameAs: [
          'https://share.google/mA5JcEXJv0lNx2yyF',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://verdevercelli.it/',
          },
        ],
      },
    ],
  },
  copy: {},
}
